import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  ArrowRight, CheckCircle2, Heart, Brain, FileText, Clock,
  Shield, TrendingUp, Users, Zap
} from 'lucide-react';

const AIConsultingHealthcare = () => {
  const useCases = [
    {
      icon: Brain,
      title: 'Clinical Decision Support',
      description: 'ML models that surface relevant patient history, flag drug interactions, and recommend evidence-based treatment pathways at the point of care.',
      outcomes: ['25% improvement in diagnostic accuracy', 'Reduced misdiagnosis rate', 'EHR-integrated real-time alerts'],
    },
    {
      icon: FileText,
      title: 'Medical Document Intelligence',
      description: 'NLP systems that extract structured data from clinical notes, discharge summaries, lab reports, and prior authorization requests automatically.',
      outcomes: ['90% reduction in manual data entry', 'Prior auth processing in minutes vs. days', 'ICD-10/CPT code suggestion'],
    },
    {
      icon: Shield,
      title: 'Healthcare AI Compliance & Security',
      description: 'HIPAA-compliant AI architecture, audit-ready data pipelines, and on-premise or private cloud deployments for sensitive clinical data.',
      outcomes: ['HIPAA-compliant by design', 'Zero PHI exposure to third-party APIs', 'Full audit trail on all AI decisions'],
    },
    {
      icon: Users,
      title: 'Patient Flow & Operations Optimization',
      description: 'Predictive scheduling, bed utilization forecasting, and staff allocation AI that reduces patient wait times and improves hospital throughput.',
      outcomes: ['40% reduction in patient wait times', '30% improvement in bed utilization', 'Reduced emergency department bottlenecks'],
    },
    {
      icon: Heart,
      title: 'Remote Patient Monitoring & Chronic Disease AI',
      description: 'AI systems that analyze wearable and IoT data streams to detect deterioration early, reduce readmissions, and support chronic disease management.',
      outcomes: ['Early deterioration detection', 'Reduced 30-day readmissions', 'Automated care plan adjustments'],
    },
    {
      icon: TrendingUp,
      title: 'Revenue Cycle & Claims Automation',
      description: 'AI-powered claims scrubbing, denial prediction, and prior authorization automation that improves clean claim rates and cash flow.',
      outcomes: ['85%+ clean claim rate', 'Denial prediction before submission', '60% faster prior auth processing'],
    },
  ];

  const caseStudies = [
    {
      title: 'AI Communication System for Regional Hospital Network',
      industry: 'Healthcare',
      result: 'Reduced clinical communication delays by 65%, improved care coordination across 8 departments.',
      link: '/case-studies/healthcare-communication',
    },
    {
      title: 'Predictive Analytics for Patient Readmission',
      industry: 'Healthcare',
      result: 'ML model predicting 30-day readmissions with 82% accuracy, enabling proactive discharge planning.',
      link: '/case-studies/healthcare-ai',
    },
  ];

  const techStack = [
    'Azure Healthcare APIs', 'AWS HealthLake', 'FHIR / HL7 integration',
    'GPT-4 with private deployment', 'Python / TensorFlow / PyTorch',
    'Databricks for clinical data pipelines', 'Power BI for clinical dashboards',
    'Secure ETL with HIPAA-compliant infrastructure',
  ];

  return (
    <>
      <Helmet>
        <title>AI Consulting for Healthcare | Clinical AI & Medical ML Solutions | Agility</title>
        <meta
          name="description"
          content="Healthcare AI consulting specialists. Clinical decision support, medical document AI, patient flow optimization, HIPAA-compliant ML systems, and revenue cycle automation. Production AI in 3-8 weeks. Serving hospitals, health systems, and healthtech companies."
        />
        <meta
          name="keywords"
          content="AI consulting healthcare, healthcare AI consulting, medical AI consulting, clinical AI solutions, HIPAA compliant AI, healthcare machine learning, medical NLP, patient flow AI, revenue cycle AI, healthcare AI company"
        />
        <link rel="canonical" href="https://www.agilitytech.ai/ai-consulting-healthcare" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="AI Consulting for Healthcare | Clinical AI & Medical ML | Agility" />
        <meta property="og:description" content="HIPAA-compliant AI consulting for healthcare. Clinical decision support, document AI, patient flow, revenue cycle automation. Production in 3-8 weeks." />
        <meta property="og:url" content="https://www.agilitytech.ai/ai-consulting-healthcare" />
        <meta property="og:image" content="https://www.agilitytech.ai/agility hex fonts.png" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            "name": "Healthcare AI Consulting",
            "provider": { "@type": "Organization", "name": "Agility", "url": "https://www.agilitytech.ai" },
            "description": "AI consulting for healthcare organizations. HIPAA-compliant clinical AI, medical document intelligence, patient flow optimization, and revenue cycle automation.",
            "areaServed": "Worldwide",
            "serviceType": "Healthcare AI Consulting",
            "audience": { "@type": "Audience", "audienceType": "Healthcare Organizations, Hospitals, Health Systems, Healthtech Companies" }
          })}
        </script>
      </Helmet>

      {/* Hero */}
      <section className="relative min-h-[80vh] flex items-center justify-center overflow-hidden bg-gradient-to-br from-blue-900 via-black to-teal-900">
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-500/10 border border-blue-500/30 rounded-full mb-8">
            <Heart className="w-4 h-4 text-blue-400" />
            <span className="text-blue-300 text-sm font-medium">Healthcare AI Consulting</span>
          </div>
          <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 leading-tight">
            AI Consulting for<br />
            <span className="text-teal-400">Healthcare</span>
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-10 leading-relaxed">
            HIPAA-compliant AI systems for hospitals, health systems, and healthtech companies.
            Clinical decision support, document intelligence, patient flow AI, and revenue cycle automation  
            delivered in 3-8 weeks.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-16">
            <Link
              to="/contact"
              className="group inline-flex items-center justify-center gap-2 bg-teal-500 hover:bg-teal-600 text-white px-8 py-4 rounded-lg font-semibold transition-all"
            >
              Get a Healthcare AI Consultation
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link
              to="/case-studies/healthcare-ai"
              className="inline-flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 text-white px-8 py-4 rounded-lg font-semibold border border-white/20 transition-all"
            >
              See Healthcare Case Studies
            </Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-4xl mx-auto">
            {[
              { value: '3-8 Weeks', label: 'Implementation', desc: 'HIPAA-compliant deployment' },
              { value: '200+', label: 'AI Projects', desc: 'Including healthcare' },
              { value: '99%', label: 'Satisfaction', desc: 'All clients' },
              { value: 'HIPAA', label: 'Compliant', desc: 'By design, always' },
            ].map((m, i) => (
              <div key={i} className="text-center">
                <div className="text-4xl font-bold text-teal-400 mb-1">{m.value}</div>
                <div className="text-white font-semibold mb-1">{m.label}</div>
                <div className="text-gray-400 text-sm">{m.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Healthcare AI Use Cases We Deliver</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Focused implementations with measurable clinical and operational outcomes.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {useCases.map((u, i) => {
              const Icon = u.icon;
              return (
                <div key={i} className="bg-gray-50 rounded-xl p-8 border border-gray-200 hover:border-teal-400 transition-all">
                  <Icon className="w-10 h-10 text-teal-600 mb-4" />
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{u.title}</h3>
                  <p className="text-gray-600 text-sm mb-5 leading-relaxed">{u.description}</p>
                  <ul className="space-y-2">
                    {u.outcomes.map((o, j) => (
                      <li key={j} className="flex items-start gap-2 text-sm text-gray-700">
                        <CheckCircle2 className="w-4 h-4 text-teal-600 mt-0.5 flex-shrink-0" />
                        {o}
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Case studies */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-gray-900 mb-12 text-center">Healthcare AI Case Studies</h2>
          <div className="grid md:grid-cols-2 gap-8">
            {caseStudies.map((cs, i) => (
              <div key={i} className="bg-white rounded-xl p-8 border border-gray-200 shadow-sm">
                <div className="text-teal-600 text-xs font-bold uppercase tracking-widest mb-3">{cs.industry}</div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{cs.title}</h3>
                <p className="text-gray-600 text-sm mb-6 leading-relaxed">{cs.result}</p>
                <Link to={cs.link} className="inline-flex items-center gap-1.5 text-teal-600 hover:text-teal-700 font-semibold text-sm group">
                  Read Case Study <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Tech stack */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-gray-900 mb-4 text-center">Healthcare AI Technology Stack</h2>
          <p className="text-lg text-gray-600 text-center max-w-2xl mx-auto mb-12">
            HIPAA-compliant infrastructure with full data sovereignty options.
          </p>
          <div className="flex flex-wrap gap-3 justify-center">
            {techStack.map((t, i) => (
              <span key={i} className="px-4 py-2 bg-gray-50 border border-gray-200 rounded-full text-gray-700 text-sm font-medium">
                {t}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Related links */}
      <section className="py-16 bg-gray-50 border-t border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-xl font-bold text-gray-900 mb-6">Related Services</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { label: 'Generative AI Consulting', href: '/generative-ai-consulting' },
              { label: 'AI Implementation Services', href: '/ai-implementation-services' },
              { label: 'AI for Insurance', href: '/ai-consulting-insurance' },
              { label: 'Healthcare AI Case Study', href: '/case-studies/healthcare-ai' },
              { label: 'AI Consulting India', href: '/ai-consulting-india' },
              { label: 'AI Consulting Ahmedabad', href: '/ai-consulting-company-ahmedabad' },
              { label: 'Data Engineering', href: '/solutions/data-engineering' },
              { label: 'All Case Studies', href: '/case-studies' },
            ].map((link, i) => (
              <Link key={i} to={link.href} className="flex items-center gap-2 text-teal-600 hover:text-teal-700 font-medium text-sm group">
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                {link.label}
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-gradient-to-br from-blue-600 to-teal-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Ready to Deploy Healthcare AI?
          </h2>
          <p className="text-xl text-white/80 mb-8 max-w-2xl mx-auto">
            Tell us your clinical or operational challenge. We'll scope a HIPAA-compliant AI solution in 48 hours.
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 bg-white text-teal-700 hover:bg-gray-100 px-8 py-4 rounded-lg font-semibold transition-all text-lg"
          >
            Talk to a Healthcare AI Consultant
            <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>
    </>
  );
};

export default AIConsultingHealthcare;
